import React, { useCallback, useEffect, useState } from 'react';
import { ContentType } from '@types';
import Video from './video/Video';
import Audio from './audio/Audio';
import Slide from './slide/Slide';
// import GoogleSlide from './google-slide/GoogleSlide';
import Form from './form/Form';
import Pdf from './pdf/Pdf';
import DefaultContent from './Default';
import { useNextContent } from '../useNextContent';
import { SllModule, SllLesson, useSllUpdateLastVisitedModuleMutation } from '@api/graphql-codegen';
import { useIntercom } from 'react-use-intercom';
import { useStandaloneContent } from '@src/hooks/useStandaloneContent';
import { useActivityTracker } from '@src/hooks/useActivity';
import NewLayoutFullScreenWrapper from '../full-screen-wrapper/NewLayoutFullScreenWrapper';
import ZipRevealSlideView from './revealslide/ZipReveal';
import { logger } from '../../../utils/logger';

type Props = {
  content?: SllModule;
  lesson?: SllLesson;
  lessons?: SllLesson[];
  goBack?: () => void;
  onExit?: () => void;
  isPreview?: boolean;
  showBanner?: boolean; // Preview banner
  lastPageNum?: number;
  isParentApp?: boolean;
  studyPlanId?: string;
  startLesson?: (lesson: SllLesson) => void;
  needFullscreen?: boolean;
  closeFullScreen?: () => void;
};

function SelectedNewLayoutContent(props: Props) {
  const {
    content, lesson, goBack, isPreview = false,
    lastPageNum, studyPlanId, needFullscreen,
    closeFullScreen, startLesson, isParentApp
  } = props;
  const { hasNextContent, nextContent, selectNext } = useNextContent();
  const { pageNumber, setPageNumber, setIsPreview } = useStandaloneContent();
  const [curContent, setCurContent] = useState<SllModule>();
  const { update } = useIntercom();
  const {pauseActivityTracker, resumeActivityTracker} = useActivityTracker();

  useEffect(() => {
    update?.({
      'hideDefaultLauncher': true
    });
    if (isPreview) {
      setIsPreview(true);
    }
    // return () => update?.({
    //   'hideDefaultLauncher': false
    // })
  }, [isPreview, setIsPreview, update])

  useEffect(() => {
    if (lastPageNum) {
      setPageNumber(lastPageNum)
    }
  }, [lastPageNum, setPageNumber])

  const [sllUpdateLastVisitedModule] = useSllUpdateLastVisitedModuleMutation();

  useEffect(() => {
    if (curContent?.id && curContent?.id !== content?.id) {
      setPageNumber(1)
    }
    setCurContent(content)
  }, [lastPageNum, curContent?.id, content, setCurContent, setPageNumber])

  useEffect(() => {
    if (!isParentApp && lesson?.id && content?.id) {
      const curIndex = lesson.presentation?.findIndex(m => m?.id === content?.id) || 0;
      const prevModule = curIndex >= 1 ? lesson.presentation?.at(curIndex - 1) : undefined;
      sllUpdateLastVisitedModule({
        variables: {
          input: {
            lessonId: lesson.id,
            moduleId: content.id,
            previousModuleId: prevModule?.id,
            pageNumber: pageNumber
          }
        }
      });
    }
  }, [lesson, content?.id, sllUpdateLastVisitedModule, pageNumber, isParentApp])

  useEffect(() => {
    if (!isPreview) {
      // Pause activity tracker when a teacher starts a lesson
      pauseActivityTracker()
    }

    // Resume activity tracker when a teacher ends a lesson
    return () => resumeActivityTracker();
  }, [isPreview, pauseActivityTracker, resumeActivityTracker])

  const onNext = useCallback(() => {
    if (hasNextContent && !nextContent?.pauseHere) {
      selectNext(content);
    }
  }, [content, hasNextContent, nextContent?.pauseHere, selectNext]);

  const handlePageNumber = useCallback((value: any) => {
    setPageNumber(value);
  }, [setPageNumber]);

  const handleStartLesson = useCallback(() => {
    if (startLesson && lesson) startLesson(lesson)
  }, [startLesson, lesson])

  const presentationContent = useCallback((isFullScreen) => {
    if (content && (content.data || content.formData || content.type === ContentType.PARENT)) {
      switch (content.type) {
        case ContentType.VIDEO: {
          return <Video data={content.data!} key={content.id} isFullScreen={isFullScreen} metaData={content} onNext={onNext} isPreview={isPreview} goBack={goBack} startLesson={handleStartLesson}/>;
        }
        case ContentType.AUDIO: {
          return <Audio data={content.data!} isFullScreen={isFullScreen} isPreview={isPreview} goBack={goBack}/>;
        }
        case ContentType.PDF: {
          return <Pdf data={content.data!} isFullScreen={isFullScreen} isPreview={isPreview} goBack={goBack}/>;
        }
        case ContentType.SLIDE: {
          return <Slide data={content.data!} isFullScreen={isFullScreen} isPreview={isPreview} goBack={goBack} pageNumber={pageNumber} changePageNumber={handlePageNumber} startLesson={handleStartLesson}/>;
        }
        case ContentType.ZIPSLIDE: {
          return (
            <ZipRevealSlideView
              data={content.data!}
              isFullScreen={isFullScreen}
              isPreview={isPreview}
              goBack={goBack}
              pageNumber={pageNumber}
              changePageNumber={handlePageNumber}
              startLesson={handleStartLesson}
            />
          );
        }
        case ContentType.FORM: {
          return <Form data={content.data!} formData={content.formData} isFullScreen={isFullScreen} id={content.id} name={content.name} isPreview={isPreview} goBack={goBack} studyPlanId={studyPlanId} lesson={lesson} pageNumber={pageNumber} changePageNumber={handlePageNumber} startLesson={handleStartLesson}/>;
        }
        default: {
          logger.debug(`[Not supported type]: `, content.type);
        }
      }
    } else if (content) {
      return <DefaultContent isFullScreen={isFullScreen} isPreview={isPreview} content={content} goBack={goBack}/>
    }
  }, [content, onNext, isPreview, goBack, pageNumber, handlePageNumber, studyPlanId, lesson, handleStartLesson]);

  return (
    <NewLayoutFullScreenWrapper lesson={lesson} content={content} needFullscreen={needFullscreen} goBack={goBack} closeFullScreen={closeFullScreen}>
      {presentationContent}
    </NewLayoutFullScreenWrapper>
  );
}

export default SelectedNewLayoutContent;
